import Alpine from 'alpinejs';

const canUseStorage = () => {
    try {
        const key = '__avari_storage_test__';
        window.localStorage.setItem(key, key);
        window.localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
};

const storageAvailable = canUseStorage();

const isDismissed = (key) => {
    if (!storageAvailable) return false;
    return window.localStorage.getItem(key) === 'true';
};

const dismiss = (key) => {
    if (!storageAvailable) return;
    window.localStorage.setItem(key, 'true');
};

const getStoredCount = (key) => {
    if (!storageAvailable) return 0;

    const count = Number.parseInt(window.localStorage.getItem(key) ?? '0', 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
};

const incrementStoredCount = (key) => {
    if (!storageAvailable) return;
    window.localStorage.setItem(key, String(getStoredCount(key) + 1));
};

Alpine.data(
    'promoBanner',
    ({ storageKey, offers = [], rotationInterval = 7000 }) => ({
        storageKey,
        offers,
        rotationInterval,
        activeIndex: 0,
        isVisible: false,
        rotationTimer: null,
        rotationPaused: false,
        resizeObserver: null,

        get availableOffers() {
            return this.offers.filter(
                (offer) =>
                    !offer.requiresGuest ||
                    (this.$store.user.isFetched && !this.$store.user.isLoggedIn)
            );
        },

        get activeOfferId() {
            return this.availableOffers[this.activeIndex]?.id ?? null;
        },

        init() {
            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() =>
                    this.updateBannerHeight()
                );
                this.resizeObserver.observe(this.$el);
            }

            this.$watch('$store.user.isFetched', () => this.syncVisibility());
            this.$watch('$store.user.isLoggedIn', () => this.syncVisibility());
            this.syncVisibility();
        },

        syncVisibility() {
            const availableOffers = this.availableOffers;
            this.isVisible =
                availableOffers.length > 0 && !isDismissed(this.storageKey);
            if (this.activeIndex >= availableOffers.length) {
                this.activeIndex = 0;
            }

            document.documentElement.classList.toggle(
                'has-promo-banner',
                this.isVisible
            );
            this.$nextTick(() => this.updateBannerHeight());
            this.startRotation();
        },

        startRotation() {
            window.clearInterval(this.rotationTimer);
            if (
                !this.isVisible ||
                this.rotationPaused ||
                this.availableOffers.length < 2 ||
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ) {
                return;
            }

            this.rotationTimer = window.setInterval(() => {
                this.activeIndex =
                    (this.activeIndex + 1) % this.availableOffers.length;
            }, this.rotationInterval);
        },

        pauseRotation() {
            this.rotationPaused = true;
            window.clearInterval(this.rotationTimer);
        },

        resumeRotation() {
            this.rotationPaused = false;
            this.startRotation();
        },

        updateBannerHeight() {
            if (!this.isVisible) {
                document.documentElement.style.removeProperty(
                    '--promo-banner-height'
                );
                return;
            }

            document.documentElement.style.setProperty(
                '--promo-banner-height',
                `${Math.ceil(this.$el.offsetHeight)}px`
            );
        },

        dismiss() {
            dismiss(this.storageKey);
            window.clearInterval(this.rotationTimer);
            this.isVisible = false;
            document.documentElement.classList.remove('has-promo-banner');
            document.documentElement.style.removeProperty(
                '--promo-banner-height'
            );
        },

        destroy() {
            window.clearInterval(this.rotationTimer);
            this.resizeObserver?.disconnect();
        }
    })
);

Alpine.data(
    'promoPopup',
    ({
        delay = 5000,
        storageKey,
        impressionKey,
        maxImpressions = 2,
        requiresGuest = true
    }) => ({
        delay,
        storageKey,
        impressionKey,
        maxImpressions,
        requiresGuest,
        timer: null,
        manuallyDismissed: false,

        init() {
            this.$watch('$store.user.isFetched', () => this.schedule());
            this.$watch('$store.user.isLoggedIn', () => this.schedule());
            this.schedule();
        },

        schedule() {
            window.clearTimeout(this.timer);

            if (
                this.manuallyDismissed ||
                isDismissed(this.storageKey) ||
                getStoredCount(this.impressionKey) >= this.maxImpressions ||
                (this.requiresGuest &&
                    (!this.$store.user.isFetched ||
                        this.$store.user.isLoggedIn))
            ) {
                return;
            }

            this.timer = window.setTimeout(() => {
                if (!this.$refs.dialog?.open) {
                    this.$refs.dialog?.showModal();
                    incrementStoredCount(this.impressionKey);
                }
            }, this.delay);
        },

        dismiss() {
            this.manuallyDismissed = true;
            dismiss(this.storageKey);
            window.clearTimeout(this.timer);
            this.$refs.dialog?.close();
        },

        handleClose() {
            window.clearTimeout(this.timer);
        }
    })
);
